<template>
  <canvas id="myCanvas"></canvas>
  <div
    class="absolute w-1/3 h-1/4 border-4 border-white text-white text-3xl font-mono top-1/2 left-1/2 font-semibold py-3 -translate-x-1/2 -translate-y-1/2 px-1 z-50 bg-black/50"
    v-if="showWarningBanner"
  >
    <div class="relative w-full break-normal select-none">
      {{ warningText }}
    </div>
    <button
      class="absolute border-4 border-white w-1/4 rounded-sm bottom-2 font-mono text-2xl -translate-x-1/2"
      @click="showWarningBanner = false"
    >
      OK
    </button>
  </div>
  <div id="description" class="h-full flex">
    <div
      :id="welcomeText.length != 0 || introPhase ? 'intro' : ''"
      :class="
        'text-white transition-all break-all px-10 capitalize text-left delay-2000 duration-1000 font-bold font-mono text-5xl ' +
        (introPhase ? 'translate-y-1/2' : 'translate-y-1/2')
      "
    >
      {{ welcomeText }}
    </div>
    <div
      :class="'transition-all delay-500 ease-in-out px-10 whitespace-pre h-full absolute w-full block'"
    >
      <div
        :class="
          'text-white break-all relative delay-200 duration-1000 capitalize text-left font-mono text-3xl leading-relaxed font-semibold ' +
          (!introPhase && welcomeText.length == 0
            ? ' top-0 mt-10 opacity-100'
            : ' mt-20 opacity-0')
        "
      >
        PRISM ID
        <div class="float-right text-white font-bold underline">
          {{ pad(lastPickedID) }}
        </div>
        <br />
        VIEW COUNT
        <div class="float-right text-white font-bold underline">
          {{ viewedTime }}
        </div>
        <br />
        CHANGE COUNT
        <div class="float-right text-white font-bold underline">
          {{ changedTime }}
        </div>
      </div>
      <textarea
        :disabled="introPhase || showWarningBanner"
        :class="
          'h-1/2 bg-black w-full py-3 px-4 flex rounded-sm shadow-xl delay-200 duration-1000 text-left text-3xl text-white font-mono resize-none border-solid border-4 border-white disabled:border-gray-500' +
          (!introPhase && welcomeText.length == 0
            ? ' mt-10 opacity-100 mb-10 pointer-events-auto'
            : ' mt-20 opacity-0')
        "
        v-model="secretText"
        placeholder="This is an empty prism, Add Your Own Message Here"
        maxlength="500"
        autofocus
      ></textarea>
      <div
        :class="
          'absolute -mt-16 right-12 text-white/50 font-mono delay-500 duration-1000' +
          (!introPhase && welcomeText.length == 0
            ? ' opacity-100 '
            : ' -mt-10 opacity-0')
        "
      >
        {{ pad(secretText.length, 3) + "/500" }}
      </div>
      <div class="w-full justify-between content-between">
        <button
          :disabled="introPhase || showWarningBanner"
          :class="
            'float-left largeButton' +
            (!introPhase && welcomeText.length == 0
              ? ' opacity-100 pointer-events-auto'
              : ' opacity-0 mt-20')
          "
          @click="updateText()"
        >
          CHANGE
        </button>
        <button
          :disabled="introPhase || showWarningBanner"
          :class="
            'float-right largeButton' +
            (!introPhase && welcomeText.length == 0
              ? ' opacity-100 pointer-events-auto'
              : ' opacity-0 mt-20')
          "
          @click="leaveSecret"
        >
          LEAVE
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
// import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { TrackballControls } from "three/addons/controls/TrackballControls.js";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { TetrahedronGeometry } from "three/src/geometries/TetrahedronGeometry.js";
import { MeshSurfaceSampler } from "three/addons/math/MeshSurfaceSampler.js";
import PocketBase from "pocketbase";

const pb = new PocketBase("https://secrets.pockethost.io");

const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(
  45,
  (window.innerWidth * 2) / window.innerHeight,
  0.0000001,
  1000
);

var renderer = new THREE.WebGLRenderer();
renderer.setSize(window.innerWidth * 2, window.innerHeight);
document.body.appendChild(renderer.domElement);

var randPosition = new THREE.Vector3(
  Math.random() * 2 - 1,
  Math.random() * 2 - 1,
  Math.random() * 2 - 1
)
  .normalize()
  .multiplyScalar(2.0);

camera.position.copy(randPosition);

const vertexShader = `
attribute vec3 barycentric;
attribute float even;
varying vec3 vBarycentric;
varying vec3 vPosition;
varying vec2 vUv;
void main () {
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position.xyz, 1.0);
  vBarycentric = barycentric;
  vPosition = position.xyz;
  vUv = uv;
}
`;

const fragmentShader = `
varying vec3 vBarycentric;
varying float vEven;
varying vec2 vUv;
varying vec3 vPosition;

uniform float time;
uniform float thickness;
uniform float secondThickness;

uniform float dashRepeats;
uniform float dashLength;
uniform bool dashOverlap;
uniform bool dashEnabled;
uniform bool dashAnimate;

uniform bool seeThrough;
uniform bool insideAltColor;
uniform bool dualStroke;

uniform bool squeeze;
uniform float squeezeMin;
uniform float squeezeMax;

uniform vec3 stroke;
uniform vec3 fill;

uniform float customAlpha;

#define PI 3.1415926538

// This is like
float aastep (float threshold, float dist) {
  float afwidth = fwidth(dist) * 0.5;
  return smoothstep(threshold - afwidth, threshold + afwidth, dist);
}

// This function is not currently used, but it can be useful
// to achieve a fixed width wireframe regardless of z-depth
float computeScreenSpaceWireframe (vec3 barycentric, float lineWidth) {
  vec3 dist = fwidth(barycentric);
  vec3 smoothed = smoothstep(dist * ((lineWidth * 0.5) - 0.5), dist * ((lineWidth * 0.5) + 0.5), barycentric);
  return 1.0 - min(min(smoothed.x, smoothed.y), smoothed.z);
}

// This function returns the fragment color for our styled wireframe effect
// based on the barycentric coordinates for this fragment
vec4 getStyledWireframe (vec3 barycentric) {
  // this will be our signed distance for the wireframe edge
  float d = min(min(barycentric.x, barycentric.y), barycentric.z);

  // for dashed rendering, we can use this to get the 0 .. 1 value of the line length
  float positionAlong = max(barycentric.x, barycentric.y);
  if (barycentric.y < barycentric.x && barycentric.y < barycentric.z) {
    positionAlong = 1.0 - positionAlong;
  }

  // the thickness of the stroke
  float computedThickness = thickness;

  // if we want to shrink the thickness toward the center of the line segment
  if (squeeze) {
    computedThickness *= mix(squeezeMin, squeezeMax, (1.0 - sin(positionAlong * PI)));
  }

  // if we should create a dash pattern
  if (dashEnabled) {
    // here we offset the stroke position depending on whether it
    // should overlap or not
    float offset = 1.0 / dashRepeats * dashLength / 2.0;
    if (!dashOverlap) {
      offset += 1.0 / dashRepeats / 2.0;
    }

    // if we should animate the dash or not
    if (dashAnimate) {
      offset += time * 0.22;
    }

    // create the repeating dash pattern
    float pattern = fract((positionAlong + offset) * dashRepeats);
    computedThickness *= 1.0 - aastep(dashLength, pattern);
  }

  // compute the anti-aliased stroke edge  
  float edge = 1.0 - aastep(computedThickness, d);

  // now compute the final color of the mesh
  vec4 outColor = vec4(0.0);
  if (seeThrough) {
    outColor = vec4(stroke, min(edge, customAlpha));
    if (insideAltColor && !gl_FrontFacing) {
      outColor.rgb = fill;
    }
  } else {
    vec3 mainStroke = mix(fill, stroke, edge);
    outColor.a = 1.0;
    if (dualStroke) {
      float inner = 1.0 - aastep(secondThickness, d);
      vec3 wireColor = mix(fill, stroke, abs(inner - edge));
      outColor.rgb = wireColor;
    } else {
      outColor.rgb = mainStroke;
    }
  }

  return outColor;
}

void main () {
  gl_FragColor = getStyledWireframe(vBarycentric);
}

`;

function addBarycentricCoordinates(bufferGeometry, removeEdge = false) {
  const attrib =
    bufferGeometry.getIndex() || bufferGeometry.getAttribute("position");
  const count = attrib.count / 3;
  const barycentric = [];

  // for each triangle in the geometry, add the barycentric coordinates
  for (let i = 0; i < count; i++) {
    const even = i % 2 === 0;
    const Q = removeEdge ? 1 : 0;
    if (even) {
      barycentric.push(0, 0, 1, 0, 1, 0, 1, 0, Q);
    } else {
      barycentric.push(0, 1, 0, 0, 0, 1, 1, 0, Q);
    }
  }

  // add the attribute to the geometry
  const array = new Float32Array(barycentric);
  const attribute = new THREE.BufferAttribute(array, 3);
  bufferGeometry.setAttribute("barycentric", attribute);
}

const lineMaterialBase = new THREE.ShaderMaterial({
  extensions: {
    // needed for anti-alias smoothstep, aastep()
    derivatives: true,
  },
  transparent: true,
  uniforms: {
    // some parameters for the shader
    time: { value: 0 },
    stroke: { value: new THREE.Color(0xecf3f4) },
    fill: { value: new THREE.Color(0xfc3a51) },
    dualStroke: { value: false },
    seeThrough: { value: true },
    insideAltColor: { value: false },
    thickness: { value: 0.12 },
    secondThickness: { value: 0.03 },
    dashEnabled: { value: false },
    dashRepeats: { value: 2.0 },
    dashOverlap: { value: true },
    dashLength: { value: 0.4 },
    dashAnimate: { value: false },
    squeeze: { value: false },
    squeezeMin: { value: 0.01 },
    squeezeMax: { value: 1.0 },
    customAlpha: { value: 1.0 },
  },
  vertexShader: vertexShader,
  fragmentShader: fragmentShader,
});

// tmp material for invisible items
const tmpMaterial = new THREE.MeshBasicMaterial({
  color: 0x000000,
  transparent: true,
  opacity: 0.0,
});

var lineMaterial = lineMaterialBase.clone();

var lineMaterialInverse = lineMaterialBase.clone();
lineMaterialInverse.uniforms.seeThrough.value = false;
// lineMaterialInverse.uniforms.fill.value = new THREE.Color(0x465461);
// lineMaterialInverse.uniforms.stroke.value = new THREE.Color(0xd4ecef);
lineMaterialInverse.uniforms.thickness.value = 0.2;
lineMaterialInverse.uniforms.secondThickness.value = 0.03;
lineMaterialInverse.uniforms.fill.value = new THREE.Color(0xffffff);
lineMaterialInverse.uniforms.stroke.value = new THREE.Color(0x000000);
lineMaterialInverse.uniforms.dualStroke.value = true;
// lineMaterialInverse.uniforms.dashEnabled.value = true;

const loader = new STLLoader();

var allTetsOriginalPos = [];
var allRands = [];
var allTetsNormal = [];
var allTimes = [];
var allTets = [];
loader.load(
  "SkullTest.stl",
  function (geometry) {
    const mesh = new THREE.Mesh(geometry);
    const sampler = new MeshSurfaceSampler(mesh).build();
    mesh.rotation.x = -Math.PI / 2;
    mesh.scale.set(0.5, 0.5, 0.5);
    mesh.geometry.center();
    const position = new THREE.Vector3();
    const normal = new THREE.Vector3();
    for (let i = 0; i < 5000; i++) {
      sampler.sample(position, normal);
      const normalCopy = normal.clone();
      allTetsNormal.push(normalCopy);
      var tet_geometry = new TetrahedronGeometry(0.048);
      addBarycentricCoordinates(tet_geometry);
      const tet_mesh = new THREE.Mesh(tet_geometry, lineMaterial);

      tet_mesh.position.x = position.x;
      tet_mesh.position.y = position.z;
      tet_mesh.position.z = -position.y;
      tet_mesh.rotation.x = Math.random() * 2 * Math.PI;
      tet_mesh.rotation.y = Math.random() * 2 * Math.PI;
      tet_mesh.rotation.z = Math.random() * 2 * Math.PI;
      tet_mesh.meshID = i;
      // tet.material = material;
      allTetsOriginalPos.push({
        x: tet_mesh.position.x,
        y: tet_mesh.position.y,
        z: tet_mesh.position.z,
      });
      allRands.push(Math.random() * 1000);
      allTimes.push(Math.random());
      scene.add(tet_mesh);
      allTets.push(tet_mesh);
    }

    // scene.add(tetGroup);
  },
  (xhr) => {
    console.log((xhr.loaded / xhr.total) * 100 + "% loaded");
  },
  (error) => {
    showWarning("Loading mesh failed");
    console.log(error);
  }
);

renderer.setClearColor(0x000000, 1);
scene.background = new THREE.Color(0x000000);
document.body.appendChild(renderer.domElement);
var d = new Date();
export default {
  name: "Secrets",
  data() {
    return {
      lastPickedID: -1, // the id of the last picke prism
      controls: null, // controls variable
      clickTransition: false, // if we are in transition to focus a prism
      clickTransitionProgress: 0, // the progress bar
      exitTransition: false, // if we are exiting focus
      exitTransitionProgress: 0, // the progress bar
      mouseDown: false, // check if mouse is pressed
      welcomeText: "", // welcome text holder
      introPhase: true, // if we are intro phase
      texts: [
        "Welcome to the prisms",
        "You can drag, zoom and rotate using your mouse",
        "Click on a prism to view its content",
        "Or press space bar to view a random one",
        "You can also edit the content within a prism",
        "Have fun",
      ],
      welcomeTextChoice: 0,
      deleteIntro: false, // if we need to delete intro at the moment
      changedTime: "N/A", // number of times this prism's text has been changed
      viewedTime: "N/A",
      secretText: "",
      secretTextOriginal: "", // the original text, for comparison to check if text is changed
      secretID: "", // the id of prism used for data transfer
      showWarningBanner: false, // if we need to show warning
      warningText: "This is a warning",
    };
  },
  watch: {},
  methods: {
    pad(n, f = 4) {
      var s = "0".repeat(f) + n;
      return s.substr(s.length - f);
    },
    showWarning(text) {
      this.warningText = text;
      this.showWarningBanner = true;
    },
    onWindowResize() {
      camera.aspect = (window.innerWidth * 2) / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth * 2, window.innerHeight);
    },
    onPointerMove(event) {
      this.mouseDown = false;
      pointer.x =
        ((event.clientX + 0.75 * window.innerWidth) / window.innerWidth / 2) *
          2 -
        1;
      pointer.y = -(event.clientY / window.innerHeight) * 2 + 1;
    },
    async updateView(id) {
      try {
        await pb.collection("posts").update(id, {
          "viewedNumber+": 1,
        });
      } catch (error) {
        this.showWarning("Failed to update view, code " + error.data.code);
        console.log("UPDATE VIEW FAILED: " + error);
      }
    },
    async updateText() {
      if (this.secretText == this.secretTextOriginal) {
        this.showWarning("You have not changed the text");
        return;
      }
      if (this.secretText.length == 0) {
        this.showWarning("You cannot leave the text empty");
        return;
      }
      if (this.secretText.length > 500) {
        this.showWarning("You cannot exceed 500 characters");
        return;
      }
      try {
        let me = this;
        const result = await pb.collection("posts").update(me.secretID, {
          text: me.secretText,
          "changedNumber+": 1,
        });
        me.changedTime = result.changedNumber;
        me.secretTextOriginal = me.secretText;
        me.showWarning("Text updated successfully");
      } catch (error) {
        this.showWarning("Failed to update text, code " + error.data.code);
        console.log(error);
      }
    },

    async mouseUp() {
      if (
        this.mouseDown &&
        this.lastPickedID != -1 &&
        !this.clickTransition &&
        !this.exitTransition
      ) {
        this.mouseDown = false;
        // we need to move the control target to the item's position
        this.clickTransition = true;

        // intro text removal
        this.introPhase = false;
        this.deleteIntro = true;
        await this.deleteText();

        // get the record
        try {
          const record = await pb
            .collection("posts")
            .getFirstListItem("prismID=" + this.lastPickedID);
          this.viewedTime = record.viewedNumber + 1;
          this.changedTime = record.changedNumber;
          this.secretText = record.text;
          this.secretTextOriginal = record.text;
          this.secretID = record.id;
          // here we increment the view number
          this.updateView(record.id);
        } catch (error) {
          this.showWarning("Failed to get record, code " + error.data.code);
          console.log(error);
        }
      }
    },

    leaveSecret() {
      if (this.clickTransition && this.clickTransitionProgress >= 1.0) {
        this.clickTransition = false;
        this.clickTransitionProgress = 0.0;
        this.exitTransition = true;
        this.exitTransitionProgress = 1.0;

        // set new camera position
        randPosition = new THREE.Vector3(
          Math.random() * 2 - 1,
          Math.random() * 2 - 1,
          Math.random() * 2 - 1
        )
          .normalize()
          .multiplyScalar(2.0);

        // add back intro phase
        this.introPhase = true;
        setTimeout(() => {
          this.deleteIntro = false;
          // this.welcomeTextChoice = 0;
          this.addText();
        }, 2000);

        for (var i = 0; i < allTets.length; i++) {
          // let's put back all other tets
          if (i != this.lastPickedID) {
            allTets[i].material = lineMaterial;
            allTets[i].scale.x = 1.0;
            allTets[i].scale.y = 1.0;
            allTets[i].scale.z = 1.0;
          }
        }
      }
    },
    async deleteText() {
      let me = this;
      if (!me.welcomeText.length == 0) {
        setTimeout(async () => {
          me.welcomeText = me.welcomeText.slice(0, -1);
          await me.deleteText();
        }, 30);
      } else {
        if (!me.deleteIntro && me.introPhase) {
          me.welcomeTextChoice = (me.welcomeTextChoice + 1) % me.texts.length;
          setTimeout(() => {
            me.addText();
          }, 1000);
        }
      }
    },

    async addText() {
      let me = this;
      if (
        me.texts[this.welcomeTextChoice].length > me.welcomeText.length &&
        !me.deleteIntro &&
        me.introPhase
      ) {
        setTimeout(async () => {
          me.welcomeText += me.texts[this.welcomeTextChoice][
            me.welcomeText.length
          ]
            ? me.texts[this.welcomeTextChoice][me.welcomeText.length]
            : "";
          await me.addText();
        }, 80);
      } else if (
        me.deleteIntro ||
        !me.introPhase ||
        me.texts[this.welcomeTextChoice].length >= me.welcomeText.length
      ) {
        setTimeout(() => {
          me.deleteText();
        }, 2000);
      }
    },
    updatePositionAndRotation(idx, intersectID, updateOthers = true) {
      // update the position of tet
      // if we are just hovering, then just the selected stayed at the position
      allTimes[idx] += intersectID != allTets[idx].meshID ? 0.015 : 0.0;
      allTets[idx].rotation.x += 0.02;
      allTets[idx].rotation.y += 0.02;
      allTets[idx].position.x =
        allTetsOriginalPos[idx].x +
        Math.sin(allTimes[idx] + allRands[idx]) * 0.2 * allTetsNormal[idx].x;
      allTets[idx].position.y =
        allTetsOriginalPos[idx].y +
        Math.sin(allTimes[idx] + allRands[idx]) * 0.2 * allTetsNormal[idx].y;
      allTets[idx].position.z =
        allTetsOriginalPos[idx].z +
        Math.sin(allTimes[idx] + allRands[idx]) * 0.2 * allTetsNormal[idx].z;
      allTets[idx].updateMatrixWorld();
    },
    previewMoveCursor() {
      if (!this.clickTransition && !this.exitTransition) {
        raycaster.setFromCamera(pointer, camera);
        // calculate objects intersecting the picking ray
        const intersects = raycaster.intersectObjects(scene.children);
        const intersectID =
          intersects.length > 0 ? intersects[0].object.meshID : -1;
        if (intersectID != this.lastPickedID) {
          if (this.lastPickedID != -1) {
            allTets[this.lastPickedID].material = lineMaterial;
          }
          if (intersectID != -1) {
            allTets[intersectID].material = lineMaterialInverse;
          }
          this.lastPickedID = intersectID;
        }
      }
    },
    async keypress(event) {
      if (
        event.key == " " &&
        this.introPhase &&
        !this.clickTransition &&
        !this.exitTransition
      ) {
        // first reset all the materials
        if (this.lastPickedID != -1) {
          allTets[this.lastPickedID].material = lineMaterial;
        }
        const intersectID = Math.floor(Math.random() * 5000);
        allTets[intersectID].material = lineMaterialInverse;
        this.lastPickedID = intersectID;

        let me = this;
        me.mouseDown = true;
        me.mouseUp();
      }
    },
    focus() {
      if (this.clickTransition) {
        // we need to move the control target to the item's position
        if (this.clickTransitionProgress < 1.0) {
          this.clickTransitionProgress += 0.01;
          this.controls.target.lerp(
            allTets[this.lastPickedID].position,
            Math.pow(this.clickTransitionProgress, 2)
          );
          var pickedPositionNorm = allTets[this.lastPickedID].position.clone();
          pickedPositionNorm.normalize();
          const farPoint = new THREE.Vector3(
            allTets[this.lastPickedID].position.x + pickedPositionNorm.x * 0.15,
            allTets[this.lastPickedID].position.y + pickedPositionNorm.y * 0.15,
            allTets[this.lastPickedID].position.z + pickedPositionNorm.z * 0.15
          );
          const closePoint = new THREE.Vector3(
            allTets[this.lastPickedID].position.x - pickedPositionNorm.x * 0.15,
            allTets[this.lastPickedID].position.y - pickedPositionNorm.y * 0.15,
            allTets[this.lastPickedID].position.z - pickedPositionNorm.z * 0.15
          );

          const lerpToPoint =
            camera.position.distanceTo(farPoint) <
            camera.position.distanceTo(closePoint)
              ? farPoint
              : closePoint;

          camera.position.lerp(
            lerpToPoint,
            Math.pow(this.clickTransitionProgress, 5)
          );
          lineMaterial.uniforms.customAlpha.value = Math.pow(
            1 - this.clickTransitionProgress,
            5
          );
          this.controls.enabled = false;
        } else {
          for (var i = 0; i < allTets.length; i++) {
            // let's remove all the other tets
            if (i != this.lastPickedID) {
              allTets[i].material = tmpMaterial;
              allTets[i].scale.x = 0.0;
              allTets[i].scale.y = 0.0;
              allTets[i].scale.z = 0.0;
            }
          }
          this.controls.enabled = true;
        }
      }
    },
    exitFocus() {
      if (this.exitTransition) {
        if (this.exitTransitionProgress > 0.0) {
          this.exitTransitionProgress -= 0.015;
          this.controls.target.lerp(
            new THREE.Vector3(0, 0, 0),
            Math.pow(1 - this.exitTransitionProgress, 5)
          );
          camera.position.lerp(
            randPosition,
            Math.pow(1 - this.exitTransitionProgress, 5)
          );
          // camera.up.lerp(
          //   new THREE.Vector3(0, 1, 0),
          //   Math.pow(1 - this.exitTransitionProgress, 5)
          // );
          lineMaterial.uniforms.customAlpha.value = Math.pow(
            1 - this.exitTransitionProgress,
            2
          );
          this.controls.enabled = false;
        } else {
          this.exitTransition = false;
          this.previewMoveCursor();
          this.controls.enabled = true;
        }
      }
    },
    animate() {
      let me = this;
      setTimeout(function () {
        requestAnimationFrame(me.animate);
      }, 1000 / 60);
      // update the picking ray with the camera and pointer position
      this.previewMoveCursor();
      this.focus();
      this.exitFocus();
      for (var i = 0; i < allTets.length; i++) {
        me.updatePositionAndRotation(i, this.lastPickedID);
      }

      d = new Date();
      const time = d.getTime();
      lineMaterialInverse.uniforms.time.value = (time / 1000.0) % 100.0;

      renderer.render(scene, camera);
      me.controls.update();
    },
  },

  mounted() {
    var myCanvas = document.getElementById("myCanvas");
    renderer = new THREE.WebGLRenderer({ canvas: myCanvas, antialias: true });
    renderer.setSize(window.innerWidth * 2, window.innerHeight);
    // control
    this.controls = new TrackballControls(camera, renderer.domElement);
    this.controls.enableDamping = true;
    this.controls.rotateSpeed = 2.5;
    this.animate();
    window.addEventListener("resize", this.onWindowResize);
    window.addEventListener("pointermove", this.onPointerMove);
    // window.addEventListener("click", this.mouseUp);
    window.addEventListener("mouseup", this.mouseUp);
    window.addEventListener("mousedown", () => {
      this.mouseDown = true;
    });
    window.addEventListener("keypress", this.keypress);
    this.addText();
  },
};
</script>

<style scoped>
#description {
  width: 50%;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.1);
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  pointer-events: none;
}

#myCanvas {
  width: 200%;
  height: 100%;
  position: absolute;
  left: -75%;
}

@keyframes cursor-blink {
  0% {
    opacity: 0;
  }
  50% {
    opacity: 0;
  }
  51% {
    opacity: 1;
  }
  100% {
    opacity: 1;
  }
}

#intro::after {
  /*code commented for brevity*/
  content: "▍";
  animation: cursor-blink 0.8s steps(2) infinite;
}
</style>
